import { Wrench, ChevronDown, Info } from "lucide-react";
import { PopoverButton } from "~/components/assistant/PopoverButton";
import type { MCPServer, McpGuidelines } from "~/components/assistant/types";
import { Button } from "~/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Switch } from "~/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

interface ToolSelectorProps {
  servers: MCPServer[];
  selectedServerIds: string[];
  onToggle: (serverId: string) => void;
  mcpGuidelines?: McpGuidelines;
}

export function ToolSelector({
  servers,
  selectedServerIds,
  onToggle,
  mcpGuidelines,
}: ToolSelectorProps) {
  const isDisabled = servers.length === 0;
  const summaryLabel =
    selectedServerIds.length > 0
      ? `${selectedServerIds.length} Tool${selectedServerIds.length !== 1 ? "s" : ""}`
      : "No Tools";

  const subagents = servers.filter((s) => !s.is_external_mcp && (s.required || s.ui_only));
  const toggleableServers = servers.filter((s) => s.is_external_mcp || (!s.required && !s.ui_only));
  const hasExternalMcps = servers.some((s) => !s.required && !s.ui_only);
  const showBanner = hasExternalMcps && mcpGuidelines?.accepted === false;

  return (
    <Tooltip delayDuration={700}>
      <Popover>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <PopoverButton
              variant="ghost"
              disabled={isDisabled}
              className="disabled:opacity-50 disabled:cursor-not-allowed has-[>svg]:px-2"
              aria-label="Manage Tools"
            >
              <Wrench className="size-4 text-gray-600" />
              <div className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-gray-700 text-[13px] text-left text-nowrap">
                <p className="block leading-[16px] whitespace-pre">{summaryLabel}</p>
              </div>
              <ChevronDown className="size-4 text-gray-400" />
            </PopoverButton>
          </PopoverTrigger>
        </TooltipTrigger>
        <PopoverContent
          className="flex w-[420px] flex-col items-start gap-2 self-stretch px-6 py-4 max-h-[450px] overflow-y-auto"
          align="start"
        >
          {showBanner && (
            <div className="self-stretch">
              <p className="text-[10px] font-semibold leading-4 tracking-[0.5px] uppercase text-gray-500 mb-1.5">
                MCP Connections
              </p>
              <div className="flex items-center gap-4 self-stretch px-3 py-2 rounded-md border border-gray-300 bg-neutral-100">
                <p className="flex-1 text-[12px] font-normal leading-[17px] text-neutral-800">
                  By using MCPs, you&#39;ll be connecting to third-party products. See our{" "}
                  <a
                    href="https://www.manifold.ai/manifold-mcp-guidelines"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-neutral-800 underline"
                  >
                    MCP guidelines
                  </a>
                  .
                </p>
                <Button
                  size="sm"
                  loading={mcpGuidelines?.isAccepting}
                  onClick={(e) => {
                    e.stopPropagation();
                    mcpGuidelines?.onAccept();
                  }}
                  className="shrink-0 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-[12px]"
                >
                  Accept
                </Button>
              </div>
            </div>
          )}
          {toggleableServers.length > 0 && (
            <div className="self-stretch">
              {toggleableServers.map((server) => (
                <div
                  key={server.id}
                  className="flex items-center justify-between gap-8 py-2 text-[13px] rounded cursor-pointer"
                  onClick={() => onToggle(server.id)}
                  aria-label="Manage Tools"
                >
                  <div className="flex flex-col">
                    <span className="text-[13px] leading-[16px] font-medium">{server.name}</span>
                    {server.description && (
                      <span className="text-[12px] leading-[16px] text-gray-600 mt-1">
                        {server.description}
                        {server.license_url && (
                          <>
                            {" "}
                            <a
                              href={server.license_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-gray-600 hover:text-foreground underline"
                            >
                              View License
                            </a>
                          </>
                        )}
                      </span>
                    )}
                  </div>
                  <Switch
                    checked={selectedServerIds.includes(server.id)}
                    className="cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                    onCheckedChange={() => onToggle(server.id)}
                  />
                </div>
              ))}
            </div>
          )}
          {subagents.length > 0 && (
            <div className="self-stretch">
              <div className="flex items-center border-t border-gray-200 pt-4 pb-2">
                <span className="text-[13px] leading-[16px] font-medium">Subagents</span>
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <Info className="ml-1 size-3.5 text-gray-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>These subagents are active by default for this dataset.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                {subagents.map((server) => (
                  <div
                    key={server.id}
                    className="flex items-center justify-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-[12px] leading-[16px] text-gray-600"
                  >
                    <span className="size-2 rounded-full bg-green-500" />
                    <span>{server.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
      <TooltipContent>
        <p>Manage Tools</p>
      </TooltipContent>
    </Tooltip>
  );
}
